import { ArrowLeft, Download, Eye } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { staffInvoiceFileName } from '@/features/finance/staff-invoices';
import {
  staffInvoiceStatusLabel,
  staffInvoiceStatusVariant,
} from '@/features/finance/staff-invoices/constants';
import { formatHoursMinutes } from '@/lib/hours';
import { formatMoney } from '@/lib/money';
import { downloadCabinetInvoice, getCabinetInvoiceBlob } from '../api';
import { useCabinetInvoice, useSignCabinetInvoice } from '../queries';

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

/** My Space → Invoices → read one + sign / download. Self-scoped server-side. */
export function CabinetInvoiceDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(false);
  const { data: inv, isPending, isError } = useCabinetInvoice(publicId);
  const sign = useSignCabinetInvoice();
  const fetchPdf = useCallback(() => getCabinetInvoiceBlob(publicId), [publicId]);

  const back = () => navigate('/me/invoices');

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !inv) {
    return (
      <div className="max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={back}>
          <ArrowLeft /> Invoices
        </Button>
        <p className="text-muted-foreground">This invoice isn’t available.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2" onClick={back}>
        <ArrowLeft /> Invoices
      </Button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{inv.number || 'Invoice'}</h1>
        <Badge variant={staffInvoiceStatusVariant(inv.status)}>
          {staffInvoiceStatusLabel(inv.status)}
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          {inv.document ? (
            <>
              <Button variant="outline" onClick={() => setPreview(true)}>
                <Eye /> View PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => downloadCabinetInvoice(inv.public_id, staffInvoiceFileName(inv))}
              >
                <Download /> Download PDF
              </Button>
            </>
          ) : null}
          {inv.status === 'issued' ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>Sign</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Sign this invoice?</AlertDialogTitle>
                  <AlertDialogDescription>
                    By signing you confirm the hours and amount are correct.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => sign.mutate(inv.public_id)}
                    disabled={sign.isPending}
                  >
                    Sign
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Period">
            {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
          </Field>
          <Field label="Hours">{formatHoursMinutes(inv.hours_worked)}</Field>
          <Field label="Total">{formatMoney(inv.total, inv.currency) ?? '—'}</Field>
          <Field label="Tax">
            {Number(inv.tax_rate) > 0
              ? `${inv.tax_rate}% · ${formatMoney(inv.tax_amount, inv.currency) ?? ''} (incl.)`
              : '—'}
          </Field>
          <Field label="Issued">{fmtDate(inv.issue_date)}</Field>
          <Field label="Due">{fmtDate(inv.due_date)}</Field>
          <Field label="Signed">{fmtDate(inv.signed_at)}</Field>
          <div className="sm:col-span-3">
            <p className="text-xs font-medium uppercase">Description</p>
            <div className="text-foreground mt-1">{inv.service_description || '—'}</div>
          </div>
          {inv.notes ? (
            <div className="sm:col-span-3">
              <p className="text-xs font-medium uppercase">Notes</p>
              <div className="text-foreground mt-1 whitespace-pre-line">{inv.notes}</div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <PdfPreviewDialog
        open={preview}
        onOpenChange={setPreview}
        title={`${inv.number || 'Invoice'} — PDF`}
        fetchBlob={inv.document ? fetchPdf : undefined}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
